/** Product-page thumbnail overflow; leaves photo selection and native touch scrolling intact. */
class ProductThumbnailRail extends HTMLElement {
  #controller;
  #observer;
  #mutationObserver;
  #frame = 0;
  #layoutPending = false;
  #viewport;
  #track;
  #photo;
  #controls;
  #previous;
  #next;
  #vertical = false;

  connectedCallback() {
    this.#viewport = this.querySelector('.slideshow-controls__thumbnails-container');
    this.#track = this.querySelector('.slideshow-controls__thumbnails');
    this.#controls = this.closest('slideshow-controls');
    this.#photo = this.closest('slideshow-component')?.querySelector('slideshow-slides');
    this.#previous = this.querySelector('[data-thumbnail-scroll="previous"]');
    this.#next = this.querySelector('[data-thumbnail-scroll="next"]');
    if (!this.#viewport || !this.#track || !this.#controls || !this.#photo) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#viewport.addEventListener('scroll', this.#scroll, { passive: true, signal });
    this.addEventListener('load', this.#schedule, { capture: true, signal });
    this.addEventListener('click', this.#click, { signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    if ('ResizeObserver' in window) {
      this.#observer = new ResizeObserver(this.#schedule);
      for (const element of [this.#photo, this.#controls, this.#track, this.#viewport]) this.#observer.observe(element);
    }
    this.#mutationObserver = new MutationObserver(this.#schedule);
    this.#mutationObserver.observe(this.#track, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#mutationObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#layoutPending = false;
  }

  #schedule = () => {
    this.#layoutPending = true;
    this.#queue();
  };

  #scroll = () => this.#queue();

  #queue() {
    if (!this.#frame) this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (!this.isConnected) return;
      if (this.#layoutPending) {
        this.#layoutPending = false;
        this.#layout();
      } else {
        this.#updateArrows();
      }
    });
  }

  #setSize(element, name, value) {
    const size = `${Math.floor(value)}px`;
    if (element.style.getPropertyValue(name) !== size) element.style.setProperty(name, size);
  }

  #layout() {
    if (!this.#viewport || !this.#track || !this.#photo || !this.#controls) return;
    // The real photo frame, not the full gallery including hints/controls, is the cap.
    const height = this.#photo.clientHeight;
    if (!height || !this.#controls.clientWidth) return;
    this.#vertical = window.matchMedia('(min-width: 750px)').matches &&
      ['left', 'right'].includes(this.#controls.getAttribute('pagination-position'));
    this.#setSize(this.#controls, '--thumbnail-rail-height', height);
    const total = this.#vertical ? this.#track.scrollHeight : this.#track.scrollWidth;
    const space = this.#vertical ? height : this.clientWidth;
    const arrowSpace = Math.min(44, Math.floor(space / 3));
    this.#setSize(this, '--thumbnail-rail-arrow-space', arrowSpace);
    const overflow = total > space + 1;
    if (this.hasAttribute('data-overflow') !== overflow) this.toggleAttribute('data-overflow', overflow);

    if (overflow) {
      const available = Math.max(1, space - arrowSpace * 2);
      // Keep at least one full thumbnail plus a visible part of the following one,
      // where space permits. Mixed image ratios are measured rather than assumed.
      const items = Array.from(this.#track.querySelectorAll('.slideshow-controls__thumbnail:not([hidden])'));
      const trackBounds = this.#track.getBoundingClientRect();
      const rtl = !this.#vertical && getComputedStyle(this).direction === 'rtl';
      let peek = available;
      for (let index = 1; index < items.length; index++) {
        const bounds = items[index].getBoundingClientRect();
        const start = this.#vertical ? bounds.top - trackBounds.top
          : rtl ? trackBounds.right - bounds.right : bounds.left - trackBounds.left;
        const extent = this.#vertical ? bounds.height : bounds.width;
        const candidate = start + Math.min(24, extent * 0.35);
        if (candidate > available) break;
        peek = candidate;
      }
      this.#setSize(this.#viewport, '--thumbnail-rail-window', peek);
    } else {
      this.#viewport.style.removeProperty('--thumbnail-rail-window');
    }
    this.#updateArrows();
  }

  #updateArrows() {
    if (!this.#previous || !this.#next || !this.#viewport) return;
    const viewport = this.#viewport.getBoundingClientRect();
    const items = this.#track.querySelectorAll('.slideshow-controls__thumbnail:not([hidden])');
    const first = items[0]?.getBoundingClientRect();
    const last = items[items.length - 1]?.getBoundingClientRect();
    const rtl = !this.#vertical && getComputedStyle(this).direction === 'rtl';
    const before = first && (this.#vertical ? first.top < viewport.top - 1
      : rtl ? first.right > viewport.right + 1 : first.left < viewport.left - 1);
    const after = last && (this.#vertical ? last.bottom > viewport.bottom + 1
      : rtl ? last.left < viewport.left - 1 : last.right > viewport.right + 1);
    this.#previous.disabled = !before;
    this.#next.disabled = !after;
  }

  #click = (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-thumbnail-scroll]');
    if (!button || !this.contains(button) || button.disabled || !this.#viewport) return;
    const direction = button.dataset.thumbnailScroll === 'previous' ? -1 : 1;
    const size = this.#vertical ? this.#viewport.clientHeight : this.#viewport.clientWidth;
    const distance = Math.max(44, size * 0.7) * direction;
    const rtl = !this.#vertical && getComputedStyle(this).direction === 'rtl';
    this.#viewport.scrollBy({
      top: this.#vertical ? distance : 0,
      left: this.#vertical ? 0 : distance * (rtl ? -1 : 1),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  };
}

if (!customElements.get('product-thumbnail-rail')) {
  customElements.define('product-thumbnail-rail', ProductThumbnailRail);
}
